import { X, UserRound } from 'lucide-react'
import { type MouseEvent, type ReactNode } from 'react'

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '#app/components/ui/alert-dialog.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { ListItem } from '#app/components/ui/list/item.tsx'
import { cn } from '#app/utils/misc.tsx'

export type EntityCardProps = {
	readonly title: string
	readonly description?: string
	readonly className?: string
	readonly onEdit?: () => void
	readonly onRemove?: () => void
	readonly removeLabel?: string
	readonly removeConfirmTitle?: string
	readonly removeConfirmDescription?: string
	readonly removeDisabled?: boolean
	readonly removeAriaLabel?: string
	readonly icon?: ReactNode
}

export function EntityCard({
	title,
	description,
	className,
	onEdit,
	onRemove,
	removeLabel = 'Remove',
	removeConfirmTitle = 'Remove this item?',
	removeConfirmDescription = 'This item will be removed from the list.',
	removeDisabled = false,
	removeAriaLabel,
	icon,
}: EntityCardProps) {
	const handleRemoveTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation()
	}

	const removeAction = onRemove ? (
		<AlertDialog>
			<AlertDialogTrigger asChild onClick={(evt) => evt.stopPropagation()}>
				<Button
					type="button"
					variant="ghost"
					size="icon"
					onClick={handleRemoveTriggerClick}
					disabled={removeDisabled}
					className={cn(
						'text-destructive hover:text-destructive focus-visible:text-destructive',
						removeDisabled ? 'opacity-40' : undefined,
					)}
					aria-label={removeAriaLabel ?? `Remove ${title}`}
				>
					<X className="h-3.5 w-3.5" aria-hidden="true" />
				</Button>
			</AlertDialogTrigger>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{removeConfirmTitle}</AlertDialogTitle>
					<AlertDialogDescription>
						{removeConfirmDescription}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel>Cancel</AlertDialogCancel>
					<AlertDialogAction
						onClick={(event) => {
							event.stopPropagation()
							if (!removeDisabled) {
								onRemove()
							}
						}}
					>
						{removeLabel}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	) : null

	return (
		<ListItem
			avatar={{
				alt: title,
				fallback: icon ?? (
					<UserRound
						className="text-muted-foreground h-3.5 w-3.5"
						aria-hidden="true"
					/>
				),
			}}
			title={<span className="truncate">{title}</span>}
			subtitle={description}
			onClick={onEdit}
			actions={removeAction}
			className={cn('select-none', className)}
		/>
	)
}
